// src/store/reducers/agencyReducer.js
import { AGENCY_ACTIONS } from '../actions/actionTypes';

const initialState = {
  agencies: [],
  selectedAgency: null,
  loading: false,
  error: null,
  success: null,
  meta: {
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 1,
  },
  filters: {
    page: 1,
    limit: 20,
    search: '',
    entityId: '',
    isOpen: undefined,
    minRating: undefined,
    city: '',
    state: '',
    lat: undefined,
    long: undefined,
    radius: 10,
    sortBy: 'name',
    sortOrder: 'asc',
  },
  agencyServices: [],
  openingHours: null,
  documents: [],
  documentsMeta: {
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 1,
  },
  uploadProgress: 0,
};

const agencyReducer = (state = initialState, action) => {
  switch (action.type) {
    // Fetch agencies
    case AGENCY_ACTIONS.FETCH_AGENCIES_REQUEST:
      return {
        ...state,
        loading: true,
        error: null,
      };

    case AGENCY_ACTIONS.FETCH_AGENCIES_SUCCESS:
      return {
        ...state,
        loading: false,
        agencies: action.payload.data || [],
        meta: action.payload.meta || initialState.meta,
        error: null,
      };

    case AGENCY_ACTIONS.FETCH_AGENCIES_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload,
        agencies: [],
      };

    // Fetch single agency
    case AGENCY_ACTIONS.FETCH_AGENCY_REQUEST:
      return {
        ...state,
        loading: true,
        error: null,
      };

    case AGENCY_ACTIONS.FETCH_AGENCY_SUCCESS:
      return {
        ...state,
        loading: false,
        selectedAgency: action.payload.data,
        error: null,
      };

    case AGENCY_ACTIONS.FETCH_AGENCY_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload,
        selectedAgency: null,
      };

    // Create agency
    case AGENCY_ACTIONS.CREATE_AGENCY_REQUEST:
      return {
        ...state,
        loading: true,
        error: null,
        success: null,
      };

    case AGENCY_ACTIONS.CREATE_AGENCY_SUCCESS:
      return {
        ...state,
        loading: false,
        agencies: [action.payload, ...state.agencies],
        success: 'Agence créée avec succès',
        error: null,
      };

    case AGENCY_ACTIONS.CREATE_AGENCY_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload,
        success: null,
      };

    // Update agency
    case AGENCY_ACTIONS.UPDATE_AGENCY_REQUEST:
      return {
        ...state,
        loading: true,
        error: null,
        success: null,
      };

    case AGENCY_ACTIONS.UPDATE_AGENCY_SUCCESS:
      return {
        ...state,
        loading: false,
        agencies: state.agencies.map(agency =>
          agency.id === action.payload.id ? action.payload : agency
        ),
        selectedAgency: action.payload,
        success: 'Agence mise à jour avec succès',
        error: null,
      };

    case AGENCY_ACTIONS.UPDATE_AGENCY_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload,
        success: null,
      };

    // Delete agency
    case AGENCY_ACTIONS.DELETE_AGENCY_REQUEST:
      return {
        ...state,
        loading: true,
        error: null,
        success: null,
      };

    case AGENCY_ACTIONS.DELETE_AGENCY_SUCCESS:
      return {
        ...state,
        loading: false,
        agencies: state.agencies.filter(agency => agency.id !== action.payload.id),
        success: action.payload.message || 'Agence supprimée avec succès',
        error: null,
      };

    case AGENCY_ACTIONS.DELETE_AGENCY_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload,
        success: null,
      };

    // Toggle agency status
    case AGENCY_ACTIONS.TOGGLE_AGENCY_STATUS_REQUEST:
      return {
        ...state,
        loading: true,
        error: null,
      };

    case AGENCY_ACTIONS.TOGGLE_AGENCY_STATUS_SUCCESS:
      return {
        ...state,
        loading: false,
        agencies: state.agencies.map(agency =>
          agency.id === action.payload.id 
            ? { ...agency, isOpen: action.payload.isOpen }
            : agency
        ),
        selectedAgency: state.selectedAgency?.id === action.payload.id
          ? { ...state.selectedAgency, isOpen: action.payload.isOpen }
          : state.selectedAgency,
        success: `Agence ${action.payload.isOpen ? 'ouverte' : 'fermée'} avec succès`,
        error: null,
      };

    case AGENCY_ACTIONS.TOGGLE_AGENCY_STATUS_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload,
      };

    // Fetch agency agencyServices
    case AGENCY_ACTIONS.FETCH_AGENCY_SERVICES_REQUEST:
      return {
        ...state,
        loading: true,
        error: null,
      };

    case AGENCY_ACTIONS.FETCH_AGENCY_SERVICES_SUCCESS:
      return {
        ...state,
        loading: false,
        agencyServices: action.payload.data || [],
        error: null,
      };

    case AGENCY_ACTIONS.FETCH_AGENCY_SERVICES_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload,
        agencyServices: [],
      };

    // Update agency agencyServices
    case AGENCY_ACTIONS.UPDATE_AGENCY_SERVICES_REQUEST:
      return {
        ...state,
        loading: true,
        error: null,
      };

    case AGENCY_ACTIONS.UPDATE_AGENCY_SERVICES_SUCCESS:
      return {
        ...state,
        loading: false,
        agencyServices: action.payload.data || action.payload,
        selectedAgency: {
          ...state.selectedAgency,
          agencyServices: action.payload.data || action.payload,
        },
        success: 'Services mis à jour avec succès',
        error: null,
      };

    case AGENCY_ACTIONS.UPDATE_AGENCY_SERVICES_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload,
      };

    // Fetch opening hours
    case AGENCY_ACTIONS.FETCH_OPENING_HOURS_REQUEST:
      return {
        ...state,
        loading: true,
        error: null,
      };

    case AGENCY_ACTIONS.FETCH_OPENING_HOURS_SUCCESS:
      return {
        ...state,
        loading: false,
        openingHours: action.payload.data || action.payload,
        error: null,
      };

    case AGENCY_ACTIONS.FETCH_OPENING_HOURS_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload,
        openingHours: null,
      };

    // Update opening hours
    case AGENCY_ACTIONS.UPDATE_OPENING_HOURS_REQUEST:
      return {
        ...state,
        loading: true,
        error: null,
      };

    case AGENCY_ACTIONS.UPDATE_OPENING_HOURS_SUCCESS:
      return {
        ...state,
        loading: false,
        openingHours: action.payload.data || action.payload,
        selectedAgency: {
          ...state.selectedAgency,
          openingHours: action.payload.data || action.payload,
        },
        success: 'Horaires mis à jour avec succès',
        error: null,
      };

    case AGENCY_ACTIONS.UPDATE_OPENING_HOURS_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload,
      };

    // Fetch agency documents
    case AGENCY_ACTIONS.FETCH_AGENCY_DOCUMENTS_REQUEST:
      return {
        ...state,
        loading: true,
        error: null,
      };

    case AGENCY_ACTIONS.FETCH_AGENCY_DOCUMENTS_SUCCESS:
      return {
        ...state,
        loading: false,
        documents: action.payload.data || [],
        documentsMeta: action.payload.meta || initialState.documentsMeta,
        error: null,
      };

    case AGENCY_ACTIONS.FETCH_AGENCY_DOCUMENTS_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload,
        documents: [],
      };

    // Upload agency document
    case AGENCY_ACTIONS.UPLOAD_AGENCY_DOCUMENT_REQUEST:
      return {
        ...state,
        loading: true,
        error: null,
        uploadProgress: 0,
      };

    case AGENCY_ACTIONS.UPLOAD_AGENCY_DOCUMENT_SUCCESS:
      return {
        ...state,
        loading: false,
        documents: [action.payload, ...state.documents],
        uploadProgress: 100,
        success: 'Document téléchargé avec succès',
        error: null,
      };

    case AGENCY_ACTIONS.UPLOAD_AGENCY_DOCUMENT_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload,
        uploadProgress: 0,
      };

    // Delete agency document
    case AGENCY_ACTIONS.DELETE_AGENCY_DOCUMENT_REQUEST:
      return {
        ...state,
        loading: true,
        error: null,
      };

    case AGENCY_ACTIONS.DELETE_AGENCY_DOCUMENT_SUCCESS:
      return {
        ...state,
        loading: false,
        documents: state.documents.filter(doc => doc.id !== action.payload.documentId),
        success: action.payload.message || 'Document supprimé avec succès',
        error: null,
      };

    case AGENCY_ACTIONS.DELETE_AGENCY_DOCUMENT_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload,
      };

    // Bulk delete
    case AGENCY_ACTIONS.BULK_DELETE_AGENCIES_REQUEST:
      return {
        ...state,
        loading: true,
        error: null,
      };

    case AGENCY_ACTIONS.BULK_DELETE_AGENCIES_SUCCESS:
      return {
        ...state,
        loading: false,
        agencies: state.agencies.filter(agency => !action.payload.ids.includes(agency.id)),
        success: action.payload.message || 'Agences supprimées avec succès',
        error: null,
      };

    case AGENCY_ACTIONS.BULK_DELETE_AGENCIES_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload,
      };

    // Set filters
    case AGENCY_ACTIONS.SET_AGENCY_FILTERS:
      return {
        ...state,
        filters: {
          ...state.filters,
          ...action.payload,
        },
      };

    // Clear states
    case AGENCY_ACTIONS.CLEAR_AGENCY_ERROR:
      return {
        ...state,
        error: null,
      };

    case AGENCY_ACTIONS.CLEAR_AGENCY_SUCCESS:
      return {
        ...state,
        success: null,
      };

    case AGENCY_ACTIONS.CLEAR_SELECTED_AGENCY:
      return {
        ...state,
        selectedAgency: null,
        agencyServices: [],
        openingHours: null,
        documents: [],
      };

    default:
      return state;
  }
};

export default agencyReducer;